
import { EventType, trackEvent } from "@/helpers/AmplitudeHelper";
import FooterMobile from "@/components/FooterMobile.vue";
import SearchMobile from "@/components/SearchMobile.vue";
import TenderCard from "@/components/TenderCard.vue";
import ThePagination from "@/components/ThePagination.vue";
import HeaderSecondary from "@/components/headers/HeaderSecondary.vue";
import SidebarSearch from "@/components/sidebars/SidebarSearch.vue";
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import EmptyCard from "@/components/EmptyCard.vue";
import { prepareQueryParams, prepareSearchPayload } from "@/helpers/SearchHelper"
import { typeRouteTranslator, regionRouteTranslator, categoryRouteTranslator, sourceValueToName } from "@/helpers/APIHelper";

export default defineComponent({
  name: "SearchResultsView",
  components: {
    HeaderSecondary,
    SidebarSearch,
    TenderCard,
    ThePagination,
    FooterMobile,
    SearchMobile,
    LoadingSpinner,
    EmptyCard,
  },
  data() {
    let pageRefreshed = false;
    if (typeof window !== 'undefined') {
      const entries = window.performance.getEntriesByType('navigation') as Array<PerformanceNavigationTiming>
      if (entries.length > 0 && entries[0].type === 'reload') {
        pageRefreshed = true;
      }
    }
    return {
      isMobile: false,
      pageTitleHeader: '',
      pageIsRefreshed: pageRefreshed,
    };
  },
  methods: {
    ...mapActions(["getNewTenders", "searchTenders", "resetFilters", "setPage", "setQuery", "setFiltersApplied", "buildCategoryDict"]),
    checkWindowSize() {
      this.isMobile = window.innerWidth <= 767;
    },
    async prebuildCategoryDict() {
      await this.buildCategoryDict();
    },
    setPageTitleHeader() {
      let titleVariable = ''
      if ('source' in this.$route.params) {
        titleVariable = sourceValueToName[this.$route.params.source as keyof typeof sourceValueToName]
        this.pageTitleHeader = `${this.$t('serp_header.source_header', { source: titleVariable })}`
        return
      }
      if ('type' in this.$route.params) {
        this.pageTitleHeader = this.$t('serp_header.type_header', { type: this.$t(`serp_header.type_${this.$route.params.type}`) });
        return 
      }
      if ('region' in this.$route.params) {
        this.pageTitleHeader = this.$t('serp_header.region_header', { region: this.$t(`serp_header.region_${this.$route.params.region}`) });
        return 
      }
      if ('category' in this.$route.params) {
        this.pageTitleHeader = this.$store.getters.categoryDict[categoryRouteTranslator[this.$route.params.category as keyof typeof categoryRouteTranslator]]
        return
      }
      this.pageTitleHeader = '';
    }
  },
  computed: {
    ...mapGetters(["getTenders", "filters", "isLoading", "query", "page", "filtersApplied", "categoryDict", "contentFilter"]),
    fetchData() {
      return this.$store.getters.filtersApplied(this.$route.name)
    },
    currentRoute () {
      return this.$route.name
    },
    language() {
      return this.$store.getters.getLanguage;
    },
    metaTitle() {
      let metaTitle = 'Ўзбекистоннинг ҳудудлари ва шаҳарлари бўйлаб харидлар ва тендерларни қидириш'
      let titleVariable = ''
      switch (this.$route.name) {
        case 'sourceSerp':
          titleVariable = sourceValueToName[this.$route.params.source as keyof typeof sourceValueToName]
          metaTitle = `Харидлар ва тендерлар сайти ${titleVariable}`
          break
        case 'typeSerp':
          titleVariable = this.$t(`serp_header.type_${this.$route.params.type}`)
          metaTitle = `Ўзбекистондаги барча ${titleVariable}`
          break
        case 'regionSerp':
          titleVariable = this.$t(`serp_header.region_${this.$route.params.region}`)
          metaTitle = `${titleVariable} харидлари ва тендерлари`
          break 
        case 'categorySerp':
          titleVariable = this.$store.getters.categoryDict[categoryRouteTranslator[this.$route.params.category as keyof typeof categoryRouteTranslator]]
          metaTitle = `Харидлар ва тендерлар категорияси ${titleVariable}`
      }
      return metaTitle
    },
    metaDescription() {
      let metaDescription = 'Аукцион, Отбор, Тендер, Фуқаролик-ҳуқуқий шартнома, Танлов, Рамкавий келишув Ўзбекистон тендер майдончаларидан: xarid.uzex.uz, etender.uzex.uz, xt-xarid.uz, tender.mc.uz, beeline.uz'
      let descriptionVariable = ''
      switch (this.$route.name) {
        case 'sourceSerp':
          descriptionVariable = sourceValueToName[this.$route.params.source as keyof typeof sourceValueToName]
          metaDescription = `Аукцион, Отбор, Тендер, Фуқаролик-ҳуқуқий шартнома, Танлов, Рамкавий келишув Узбекистон тендер майдончаларидан: ${descriptionVariable}`
          break
        case 'typeSerp':
          descriptionVariable = this.$t(`serp_header.type_${this.$route.params.type}`)
          metaDescription = `Бепул қидиринг ${descriptionVariable} ва Telegram орқали хабарлар олинг. Тендер майдончалари: xarid.uzex.uz, etender.uzex.uz, xt-xarid.uz, tender.mc.uz, beeline.uz`
          break
        case 'regionSerp':
          descriptionVariable = this.$t(`serp_header.region_${this.$route.params.region}`)
          metaDescription = `Аукцион, Отбор, Тендер, Фуқаролик-ҳуқуқий шартнома, Танлов, Рамкавий келишув ${descriptionVariable}да`
          break
        case 'categorySerp':
          descriptionVariable = this.$store.getters.categoryDict[categoryRouteTranslator[this.$route.params.category as keyof typeof categoryRouteTranslator]]
          metaDescription = `Аукцион, Отбор, Тендер, Фуқаролик-ҳуқуқий шартнома, Танлов, Рамкавий келишув ${descriptionVariable} категориясида`
          break
      }
      return metaDescription
    },
    metaCanonicalLink() {
      const { path, query } = this.$route

      const sortedKeys = Object.keys(query).sort()
      const searchParams = new URLSearchParams()
      for (const key of sortedKeys) {
        const val = query[key]
        if (val == null) continue
        searchParams.append(key, val as string)
      }

      const qs = searchParams.toString()
      return `https://tender.asia${path}${qs ? `?${qs}` : ''}`
    },
  },
  watch: {
    async fetchData(newValue) {
      if (newValue) {
        const queryParams = prepareQueryParams(1, this.query, this.filters, this.contentFilter)

        let pushToMainSerp = false
        for (const key in this.$route.params) {
          if ((key in this.filters && key in this.contentFilter && this.filters[key] !== this.contentFilter[key]) 
          || !(key in this.filters)) {
            pushToMainSerp = true
          }
        }

        if (pushToMainSerp) {
          this.$router.push({
            name: 'tenderSerp',
            query: queryParams
          })
        } else {
          this.$router.push({
            name: this.$router.currentRoute.value.name as string,
            query: queryParams
          })
        }
        this.setFiltersApplied({ applied: false })
      }
    },
    "$route.query": {
      async handler(queryParams) {
        this.setPage({ page: queryParams["page"] })

        const categoryOption = this.$store.getters.categoryOption
        this.resetFilters({ shouldApplyFilter: false })

        const routeParams = this.$route.params
        if ("source" in routeParams) {
          const filterPayload = {
            key: "source",
            value: sourceValueToName[this.$route.params.source as keyof typeof sourceValueToName]
          }
          this.$store.dispatch("updateFilter", filterPayload);
          this.$store.dispatch("updateContentFilter", filterPayload)
        }

        if ("type" in routeParams) {
          const filterPayload = {
            key: "type",
            value: typeRouteTranslator[routeParams.type as keyof typeof typeRouteTranslator]
          }
          this.$store.dispatch("updateFilter", filterPayload);
          this.$store.dispatch("updateContentFilter", filterPayload)
        }

        if ("region" in routeParams) {
          const filterPayload = {
            key: "region",
            value: regionRouteTranslator[routeParams.region as keyof typeof regionRouteTranslator]
          }
          this.$store.dispatch("updateFilter", filterPayload)
          this.$store.dispatch("updateContentFilter", filterPayload)
        }

        if ("category" in routeParams) {
          const categoryCode = categoryRouteTranslator[routeParams.category as keyof typeof categoryRouteTranslator]
          const filterPayload = {
            key: "category",
            value: categoryCode
          }
          this.$store.dispatch("updateFilter", filterPayload)
          this.$store.dispatch("updateContentFilter", filterPayload)
          this.$store.dispatch(
            'updateCategoryOption', 
            `${categoryCode} ${this.categoryDict[categoryCode]}`
          )
        }

        if ("q" in queryParams) {
          this.setQuery({ query: queryParams["q"] })
        } else {
          this.setQuery({ query: '' })
        }

        if ("country" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "country",
            value: queryParams["country"] as string
          });
        }

        if ("region" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "region",
            value: queryParams["region"] as string
          });
        }

        if ("type" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "type",
            value: queryParams["type"] as string
          });
        }

        if ("priceFrom" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "priceFrom",
            value: Number(queryParams["priceFrom"])
          });
        }

        if ("priceTo" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "priceTo",
            value: Number(queryParams["priceTo"])
          });
        }

        if ("startDateFrom" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "startDateFrom",
            value: new Date(`${(queryParams["startDateFrom"] as string)}T00:00:00`)
          });
        }

        if ("startDateTo" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "startDateTo",
            value: new Date(`${(queryParams["startDateTo"] as string)}T00:00:00`)
          });
        }

        if ("endDateFrom" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "endDateFrom",
            value: new Date(`${(queryParams["endDateFrom"] as string)}T00:00:00`)
          });
        }

        if ("endDateTo" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "endDateTo",
            value: new Date(`${(queryParams["endDateTo"] as string)}T00:00:00`)
          });
        }

        if ("company" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "company",
            value: queryParams["company"] as string
          });
        }

        if ("source" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "source",
            value: queryParams["source"] as string
          });
        }

        if ("category" in queryParams) {
          this.$store.dispatch("updateCategoryOption", categoryOption)
          this.$store.dispatch("updateFilter", {
            key: "category",
            value: queryParams["category"] as string
          })
        }

        if ("status" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "status",
            value: queryParams["status"] as string
          })
        }

        let pageRefresh = false;
        if (this.pageIsRefreshed) {
          pageRefresh = true
          this.pageIsRefreshed = false
        }

        const searchPayload = prepareSearchPayload(this.page, this.query, this.filters, undefined, pageRefresh)
        if (!this.query || this.query === '') {
          await this.getNewTenders(searchPayload);
        } else {
          await this.searchTenders(searchPayload);
        }
      },
      immediate: true,
    },
    language: {
      async handler() {
        await this.prebuildCategoryDict()
        this.setPageTitleHeader()
      }
    }
  },
  async beforeRouteEnter(to, from, next) {
    const queryParams = { ...to.query };
    if (!("page" in queryParams)) {
      queryParams["page"] = "1";
      next({ name: to.name as string, query: queryParams, params: to.params });
    } else {
      next();
    }
  },
  async created() {
    const bodyElementClasses = document.body.classList;
    bodyElementClasses.remove(...bodyElementClasses);
    bodyElementClasses.add("sidebar-wo-footer");
    bodyElementClasses.add("is-tenders-list");

    if (this.$route.name !== 'tenderSerp') {
      if (Object.keys(this.categoryDict).length === 0) {
        await this.prebuildCategoryDict();
        this.setPageTitleHeader();
      } else {
        this.setPageTitleHeader();
      }
    }
    await trackEvent(EventType.search_page_opened)
  },
  mounted() {
    this.checkWindowSize();
    window.addEventListener('resize', this.checkWindowSize);
  },
  unmounted() {
    this.setQuery({ query: '' })
    window.removeEventListener('resize', this.checkWindowSize);
  },
  head() {
    return {
      title: this.metaTitle,
      meta: [
        { name: "description", content: this.metaDescription }
      ],
      link: [
        { rel: "canonical", href: this.metaCanonicalLink }
      ]
    }
  }
});
